import { Tooltip } from 'antd';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Delete } from '@material-ui/icons';
import {
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/styles';
import { v4 as uuidv4 } from 'uuid';
import Modal from '../../MaterialUIModal';
import FolderDeleteConfirmationModal from './FolderDeleteConfirmationModal';
import SaveAndPublishConfirmationModal from './SaveAndPublishConfirmationModal';
import { Row, Column, Content, SmallText } from './SavedRowsModal.styled';
import { saveFolder } from '../savedRows/api';
import {
  prefixFolderGlyph,
  PUBLISHER_GLYPH,
  UNIVERSAL_GLYPH,
} from './folderGlyphs';
import { useToast } from '../../../redux/action';
import { userPrivilegesSelector } from '../../../redux/selector';

const useStyles = makeStyles(() => ({
  selectField: {
    color: '#212121',
    '&:focus': {
      backgroundColor: 'transparent',
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '&.Mui-focused fieldset': {
        borderColor: '#007ade',
        borderWidth: '2px',
      },
    },
    '& .MuiInputBase-input': {
      color: '#212121',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
  },
}));

const SavedRowsModal = ({
  resolve,
  args,
  setDynamicRowsConfiguration,
  dynamicRowsConfiguration,
  orgRows,
}) => {
  const { row, onHide, isNew } = args;
  const classes = useStyles();

  useEffect(() => {
    if (isNew) {
      row.metadata = {};
    }
  }, []);
  const { errorToast, successToast } = useToast();
  const { isSA, isSU } = useSelector(userPrivilegesSelector);
  // Superuser and above may designate a folder as a Publisher Folder. The
  // privilege seed cascades, so isSU is true for Super User / Admin / Duper.
  const canMakePublisherFolder = isSU;

  const [rowName, setRowName] = useState('');
  const [folderName, setFolderName] = useState('');
  const [tempFolderName, setTempFolderName] = useState('');
  const [folderId, setFolderId] = useState('');
  const [
    isRowCategoryDeleteConfirmationModalVisible,
    setRowCategoryDeleteConfirmationModalVisibility,
  ] = useState(false);
  const [folderNames, setFolderNames] = useState(
    dynamicRowsConfiguration.externalContentURLs || []
  );
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isUniversal, setUniversal] = useState(false);
  const [isPublisherFolder, setPublisherFolder] = useState(false);
  const [isExistingFolderSelected, setExistingFolderSelected] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const updateRowName = event => setRowName(event.target.value);

  // "Make Universal" and "Make Publisher Folder" are mutually exclusive.
  const handleUniversalToggleChange = event => {
    const checked = event.target.checked;
    setUniversal(checked);
    if (checked) setPublisherFolder(false);
  };

  const handlePublisherToggleChange = event => {
    const checked = event.target.checked;
    setPublisherFolder(checked);
    if (checked) setUniversal(false);
  };

  const isFolderIdValid = () => {
    if (
      !row?.metadata?.folderId ||
      !dynamicRowsConfiguration?.externalContentURLs
    ) {
      return false;
    }

    return dynamicRowsConfiguration.externalContentURLs.some(
      folder => folder.handle === row.metadata.folderId
    );
  };

  useEffect(() => {
    setFolderNames(dynamicRowsConfiguration.externalContentURLs);
    const isValid = isFolderIdValid();
    if (isValid) {
      setRowName(row.metadata.name);
      setFolderId(row?.metadata?.folderId);
      setFolderName(row?.metadata?.folder);
    }
  }, [dynamicRowsConfiguration]);

  // Whether the resolved folder is a Publisher Folder: true if the user just
  // toggled it on for a new folder, or the selected existing folder is one.
  const isResolvedFolderPublisher = id =>
    isPublisherFolder ||
    (dynamicRowsConfiguration.externalContentURLs || []).some(
      folder =>
        (folder.handle === id || folder.value === id) &&
        folder.isPublisherFolder
    );

  // Returns true once the row is resolved and the dialog has been closed via
  // onHide, false if nothing could be saved. Callers use this to avoid
  // revealing the "New Saved Row" form again after a successful save.
  const performSave = async () => {
    if (rowName && folderName && folderId) {
      resolve({
        name: rowName,
        folder: folderName,
        folderId: folderId,
      });
      onHide();
      if (isNew) {
        successToast('Successfully saved a new row.');
      } else {
        successToast('Saved row successfully updated.');
      }
      return true;
    } else if (rowName && tempFolderName) {
      const created = await createFolderIfItDoesNotExist(tempFolderName);
      if (!created?.folderId) {
        errorToast('Encountered an error while saving a folder.');
        return false;
      }
      resolve({
        name: rowName,
        folder: created.folderName,
        folderId: created.folderId,
      });
      onHide();
      successToast('Successfully saved a new row.');
      return true;
    }
    return false;
  };

  const handleSave = () => {
    const willSave =
      (rowName && folderName && folderId) || (rowName && tempFolderName);
    if (!willSave) return;
    // New saves into a Publisher Folder require explicit confirmation first,
    // before any folder is created, the row is resolved, or toasts fire.
    if (isNew && isResolvedFolderPublisher(folderId)) {
      setShowPublishConfirm(true);
      return;
    }
    performSave();
  };

  const handleCancel = () => {
    resolve('Save cancelled');
    onHide();
  };

  const handleFolderChange = value => {
    try {
      const { name, id } = JSON.parse(value);
      setFolderName(name);
      setFolderId(id);
    } catch (error) {
      console.error('Error parsing folder value:', error);
    }
  };

  const createNewFolder = async newFolderName => {
    try {
      // Bake the type glyph into the persisted name: ⁂ for universal, 🌐︎ for
      // publisher. The glyph lives in the DB name and shows in BeeFree's panel.
      const updatedFolderName = prefixFolderGlyph(newFolderName, {
        isUniversal,
        isPublisherFolder,
      });
      setFolderName(updatedFolderName);
      const folderId = uuidv4();

      setFolderNames([
        ...folderNames,
        { name: updatedFolderName, handle: folderId },
      ]);

      setFolderId(folderId);

      await saveFolder({
        name: updatedFolderName,
        id: folderId,
        isUniversal,
        isPublisherFolder,
      });

      handleFolderChange(
        JSON.stringify({ name: updatedFolderName, id: folderId })
      );
      setDynamicRowsConfiguration(prevConfig => ({
        ...prevConfig,
        externalContentURLs: [
          ...prevConfig.externalContentURLs,
          {
            name: updatedFolderName,
            id: folderId,
            handle: folderId,
            isLocal: true,
            behaviors: { canEdit: true, canDelete: true },
            isUniversal,
            isPublisherFolder,
          },
        ],
      }));
      successToast('Successfully created a new folder.');
      return { folderId, folderName: updatedFolderName };
    } catch (e) {
      console.error('Error in handleSave:', e);
      errorToast('Encountered an error while saving a folder.');
    }
  };

  const createFolderIfItDoesNotExist = async inputValue => {
    const trimmed = inputValue?.trim() ?? '';
    if (trimmed === '') return undefined;
    // Match the name the new folder WOULD be saved under: prefixFolderGlyph
    // strips any preview glyph off the typed value and re-applies the current
    // type's. This makes the dedupe type-aware (a user-typed "Foo" won't collide
    // with a Universal "⁂ Foo") and robust to whether the typed value arrived
    // with a glyph.
    const candidateName = prefixFolderGlyph(trimmed, {
      isUniversal,
      isPublisherFolder,
    });
    const existingCategory = folderNames.find(
      cat => cat.name.toLowerCase() === candidateName.toLowerCase()
    );
    if (existingCategory) {
      return {
        folderId: existingCategory.handle,
        folderName: existingCategory.name,
      };
    }
    return createNewFolder(inputValue);
  };

  const renderModal = () => {
    if (showPublishConfirm) {
      return (
        <SaveAndPublishConfirmationModal
          onConfirm={async () => {
            // Keep this confirmation up while the save runs. On success
            // performSave() closes the whole dialog via onHide, so we must not
            // pre-hide it and fall through to the "New Saved Row" form. Only
            // return to the form if the save failed.
            const saved = await performSave();
            if (!saved) setShowPublishConfirm(false);
          }}
          onCancel={() => setShowPublishConfirm(false)}
        />
      );
    }
    if (isRowCategoryDeleteConfirmationModalVisible) {
      return (
        <FolderDeleteConfirmationModal
          categoryToDelete={categoryToDelete}
          setDynamicRowsConfiguration={setDynamicRowsConfiguration}
          setRowCategoryDeleteConfirmationModalVisibility={
            setRowCategoryDeleteConfirmationModalVisibility
          }
          onDeleted={handleCancel}
        />
      );
    } else {
      return (
        <Modal
          className="templatemodal save-as-modal"
          title="New Saved Row"
          subtitle="Save and reuse your favorite rows. Give it a name, then keep it organized in a folder."
          onClose={handleCancel}
          primaryButton={{
            label: 'Save',
            onClick: handleSave,
            isDisabled: !rowName || !(folderName || tempFolderName),
          }}
        >
          <Column>
            <TextField
              label="Name"
              variant="outlined"
              size="small"
              value={rowName}
              onChange={updateRowName}
              fullWidth
              className={classes.textField}
            />
            <Autocomplete
              fullWidth
              size="small"
              className={classes.textField}
              options={folderNames}
              freeSolo={true}
              getOptionLabel={option =>
                typeof option === 'string' ? option : option.name ?? ''
              }
              value={folderName ? { name: folderName, id: folderId } : null}
              onChange={(event, newValue) => {
                if (newValue) {
                  setUniversal(false);
                  setPublisherFolder(false);
                  setExistingFolderSelected(true);
                  handleFolderChange(
                    JSON.stringify({
                      name: newValue.name,
                      id: newValue.handle,
                    })
                  );
                } else {
                  setExistingFolderSelected(false);
                  setTempFolderName('');
                  setFolderName('');
                  setFolderId('');
                }
              }}
              onInputChange={(event, newInputValue) => {
                setTempFolderName(newInputValue ? newInputValue.trim() : null);
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  const inputValue = event.target.value;
                  createFolderIfItDoesNotExist(inputValue);
                }
              }}
              renderInput={params => {
                // While creating (a toggle is on), preview the type glyph in
                // front of the typed name. Otherwise show the value as-is, so an
                // existing folder selected from the dropdown keeps its glyph
                // (prefixFolderGlyph strips when no toggle is set).
                const rawValue = params.inputProps.value || '';
                const displayValue =
                  isUniversal || isPublisherFolder
                    ? prefixFolderGlyph(rawValue, {
                        isUniversal,
                        isPublisherFolder,
                      })
                    : rawValue;

                return (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      value: displayValue,
                    }}
                    label="Folder"
                    variant="outlined"
                    className={classes.selectField}
                  />
                );
              }}
              renderOption={option => {
                // Reuse the per-folder behaviors computed by getFolders: a
                // Publisher/Universal folder the user can't manage comes back
                // with canDelete false (Staff Support, non-owning orgs, etc.).
                const cannotDelete = option.behaviors?.canDelete !== true;
                const isNonEmpty = Boolean(orgRows[option.handle]?.length);
                const isDisabled = cannotDelete || isNonEmpty;
                const tooltipTitle = cannotDelete
                  ? "You don't have permission to delete this folder."
                  : 'Permanently deletes this folder. Folder must be empty of all saved rows.';
                return (
                  <Row>
                    {option.name}
                    <Tooltip title={tooltipTitle}>
                      <span>
                        <IconButton
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isDisabled) {
                              errorToast(
                                cannotDelete
                                  ? "You don't have permission to delete this folder."
                                  : "Can't delete folder.  Folder must be empty of all saved rows."
                              );
                            } else {
                              setCategoryToDelete(option);
                              setRowCategoryDeleteConfirmationModalVisibility(
                                true
                              );
                            }
                          }}
                        >
                          <Delete
                            fontSize="small"
                            style={{
                              color: isDisabled
                                ? 'rgba(0, 0, 0, 0.26)'
                                : 'grey',
                            }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Row>
                );
              }}
            />
            {isSA &&
              !isExistingFolderSelected && ( // only allowing a new folder to be set universal
                <FormControlLabel
                  control={
                    <Switch
                      checked={isUniversal}
                      onChange={handleUniversalToggleChange}
                      color="primary"
                    />
                  }
                  label={
                    <Content>
                      <span>{`${UNIVERSAL_GLYPH} Make Universal`}</span>
                      <SmallText>
                        Makes this folder and its saved rows available to all
                        organizations.
                      </SmallText>
                    </Content>
                  }
                />
              )}
            {canMakePublisherFolder &&
              !isExistingFolderSelected && ( // only a new folder can be made a publisher folder
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPublisherFolder}
                      onChange={handlePublisherToggleChange}
                      color="primary"
                    />
                  }
                  label={
                    <Content>
                      <span>{PUBLISHER_GLYPH} Make Publisher Folder</span>
                      <SmallText>
                        Publishes this folder&apos;s saved rows to all
                        NoteRouter customers. Cannot be combined with Make
                        Universal.
                      </SmallText>
                    </Content>
                  }
                />
              )}
          </Column>
        </Modal>
      );
    }
  };

  return <Column>{renderModal()}</Column>;
};

export default SavedRowsModal;
